import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useAuth } from "hooks/useAuth";
import {
  getPageGuidePreferences,
  savePageGuidePreferences,
} from "services/preferences/pageGuidePreferencesService";
import { fetchPageGuidePlatformStatus } from "services/pageGuide/pageGuideService";
import { unlockPageGuideAudio } from "components/pageGuide/pageGuideAudio";

const PageGuideContext = createContext(null);

export function PageGuideProvider({ children }) {
  const { user } = useAuth();
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [pageOffered, setPageOffered] = useState(false);
  const [hasIntroduction, setHasIntroduction] = useState(false);
  const [introPlaying, setIntroPlaying] = useState(false);
  const [pageIdOverride, setPageIdOverrideState] = useState(null);
  const [introControllers, setIntroControllers] = useState({
    play: null,
    stop: null,
  });
  const [userEnabled, setUserEnabledState] = useState(false);
  const [heardCalibration, setHeardCalibration] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) {
      setUserEnabledState(false);
      setHeardCalibration(false);
      setPrefsLoaded(true);
      setPlatformEnabled(false);
      setStatusLoaded(true);
      return undefined;
    }

    setPrefsLoaded(false);
    setStatusLoaded(false);

    (async () => {
      try {
        const [prefs, status] = await Promise.all([
          getPageGuidePreferences(user.uid),
          fetchPageGuidePlatformStatus().catch(() => ({ platformEnabled: true })),
        ]);
        if (cancelled) return;
        setUserEnabledState(prefs.enabled === true);
        setHeardCalibration(prefs.heardCalibration === true);
        setPlatformEnabled(status.platformEnabled !== false);
      } finally {
        if (!cancelled) {
          setPrefsLoaded(true);
          setStatusLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const setUserEnabled = useCallback(
    async (enabled) => {
      if (!user?.uid) return;
      unlockPageGuideAudio();
      setUserEnabledState(Boolean(enabled));
      await savePageGuidePreferences(user.uid, { enabled: Boolean(enabled) });
      if (enabled && !heardCalibration) {
        setCalibrationOpen(true);
      }
    },
    [user?.uid, heardCalibration],
  );

  const completeCalibration = useCallback(
    async (heard) => {
      if (!user?.uid) return;
      setHeardCalibration(true);
      setCalibrationOpen(false);
      await savePageGuidePreferences(user.uid, {
        heardCalibration: true,
        enabled: true,
      });
      setUserEnabledState(true);
      if (!heard) {
        // Caller shows guidance UI; prefs already saved.
      }
    },
    [user?.uid],
  );

  const reportPageOffered = useCallback((offered) => {
    setPageOffered(Boolean(offered));
  }, []);

  const reportIntroduction = useCallback((hasIntro) => {
    setHasIntroduction(Boolean(hasIntro));
  }, []);

  const registerIntroControllers = useCallback((controllers) => {
    setIntroControllers({
      play: typeof controllers?.play === "function" ? controllers.play : null,
      stop: typeof controllers?.stop === "function" ? controllers.stop : null,
    });
  }, []);

  const setPageIdOverride = useCallback((pageId) => {
    setPageIdOverrideState(
      typeof pageId === "string" && pageId.trim() ? pageId.trim() : null,
    );
  }, []);

  const playIntroduction = useCallback(async () => {
    if (typeof introControllers.play === "function") {
      await introControllers.play();
    }
  }, [introControllers.play]);

  const stopIntroduction = useCallback(() => {
    if (typeof introControllers.stop === "function") {
      introControllers.stop();
    }
  }, [introControllers.stop]);

  const refreshPlatformStatus = useCallback(async () => {
    if (!user?.uid) {
      setPlatformEnabled(false);
      return false;
    }
    try {
      const status = await fetchPageGuidePlatformStatus();
      const enabled = status.platformEnabled !== false;
      setPlatformEnabled(enabled);
      return enabled;
    } catch {
      setPlatformEnabled(true);
      return true;
    }
  }, [user?.uid]);

  const value = useMemo(
    () => ({
      platformEnabled,
      pageOffered,
      hasIntroduction,
      introPlaying,
      setIntroPlaying,
      playIntroduction,
      stopIntroduction,
      registerIntroControllers,
      reportIntroduction,
      pageIdOverride,
      setPageIdOverride,
      userEnabled,
      setUserEnabled,
      heardCalibration,
      calibrationOpen,
      setCalibrationOpen,
      completeCalibration,
      reportPageOffered,
      refreshPlatformStatus,
      prefsLoaded,
      statusLoaded,
      ready: prefsLoaded && statusLoaded,
    }),
    [
      platformEnabled,
      pageOffered,
      hasIntroduction,
      introPlaying,
      playIntroduction,
      stopIntroduction,
      registerIntroControllers,
      reportIntroduction,
      pageIdOverride,
      setPageIdOverride,
      userEnabled,
      setUserEnabled,
      heardCalibration,
      calibrationOpen,
      completeCalibration,
      reportPageOffered,
      refreshPlatformStatus,
      prefsLoaded,
      statusLoaded,
    ],
  );

  return (
    <PageGuideContext.Provider value={value}>{children}</PageGuideContext.Provider>
  );
}

PageGuideProvider.propTypes = {
  children: PropTypes.node,
};

export function usePageGuideContext() {
  const ctx = useContext(PageGuideContext);
  if (!ctx) {
    throw new Error("usePageGuideContext must be used within PageGuideProvider");
  }
  return ctx;
}

/** Safe for chrome that may render outside provider during tests. */
export function usePageGuideContextOptional() {
  return useContext(PageGuideContext);
}
